import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Checkbox, HelpTooltip, Percent } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useFeatures, useStable } from '~/hooks';
import { colors, weights } from '~/styles';
import { QueryString } from '~/utils';

const Container = styled.div`
  flex: 1;
  display: flex;
  margin: 0.25rem;
  background-color: ${colors.white};
  border-radius: 0.625rem;
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
`;

const Summary = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.25rem 0rem 1.5rem 0rem;
`;

const Title = styled.div`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  padding-left: 1.25rem;
`;

const SummaryValue = styled.div`
  color: ${colors.primary};
  font-size: 2.25rem;
  font-weight: ${weights.light};
`;

const SummaryLabel = styled.div`
  margin-top: 0.25rem;
  color: ${colors.grey25};
  font-size: 0.625rem;
  text-align: center;
  text-transform: uppercase;
`;

const SummaryInfo = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem 0;

  &:hover ${SummaryValue} {
    color: ${colors.accent};
  }
`;

const Breakdowns = styled.div`
  display: flex;
  flex-direction: column;
`;

const BreakdownLabel = styled.div`
  color: ${colors.grey40};
  font-size: 0.625rem;
`;

const BreakdownValue = styled.div`
  color: ${colors.black};
  font-size: 0.875rem;
  font-weight: ${weights.bold};
`;

const BreakdownItem = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  padding-right: 2rem;
  border: solid 0.125rem ${colors.grey5};
  border-right: none;
  border-bottom: none;

  &:first-child {
    border-top: none;
  }

  &:hover ${BreakdownValue} {
    color: ${colors.grey55};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  border-top: solid 0.125rem ${colors.grey5};
  width: 100%;
  padding: 1.5rem 0.75rem 0rem 0.75rem;
  justify-content: center;
  align-items: center;
  font-size: 0.875rem;

  & > label {
    & > div {
      height: 1.25rem;
      width: 1.25rem;
      & > svg {
        height: 0.75rem;
        width: 0.75rem;
      }
    }
  }
`;

export default function Utilization({ entries, startDate, endDate, member: selectedMember }) {
  const features = useFeatures();
  const { workspace } = useWorkspace();
  const [adjustForTimeOff, setAdjustForTimeOff] = useState(workspace.defaultAdjustForTimeOff);

  const member = useMemo(() => {
    return selectedMember ?? workspace.member;
  }, [selectedMember, workspace]);

  const api = useApi();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!features.utilization) return;

    try {
      const { data } = await api.www.workspaces(workspace.id).timeEntries().getUtilizationSummary({
        start: startDate,
        end: endDate,
        memberId: member?.id,
        adjustForTimeOff,
      });

      setData(data);
    } catch (error) {
      setData(null);
    }
  }, [features.utilization, api, workspace.id, startDate, endDate, member, adjustForTimeOff]);

  const stableEntries = useStable(entries);
  useEffect(() => {
    fetchData();
  }, [fetchData, stableEntries]);

  const utilizationByMember = (query = {}) =>
    `/app/${workspace.key}/reports/utilization-by-member${new QueryString(
      {
        start: startDate,
        end: endDate,
        memberId: member?.id,
        memberBillableTypeId: 'all',
        memberEmploymentTypeId: 'all',
        utilizationMode: adjustForTimeOff ? 'time_off_adjusted' : 'default',
        ...query,
      },
      { multi: true },
    ).toString(true)}`;

  if (!features.utilization) return null;
  if (!data) return <Container />;

  const getData = (timeOffAdjusted) => {
    if (timeOffAdjusted) {
      return {
        billableUtilization: data.billableUtilizationTimeOffAdjusted,
        targetBillable: data.targetBillableTimeOffAdjusted,
        attainment: data.attainmentTimeOffAdjusted,
      };
    }

    return {
      billableUtilization: data.billableUtilization,
      targetBillable: data.targetBillable,
      attainment: data.attainment,
    };
  };

  const { billableUtilization, targetBillable, attainment } = getData(adjustForTimeOff);

  return (
    <Container>
      <Summary>
        <Title>Utilization</Title>
        <SummaryInfo to={utilizationByMember()}>
          <SummaryValue>
            <Percent value={billableUtilization} minimumFractionDigits={1} maximumFractionDigits={1} />
          </SummaryValue>
          <SummaryLabel>Billable Utilization</SummaryLabel>
        </SummaryInfo>
        <CheckboxContainer>
          <Checkbox
            label="Adjust for Time Off"
            checked={adjustForTimeOff}
            onChange={() => setAdjustForTimeOff(!adjustForTimeOff)}
            style={{ height: 0 }}
          />
          <HelpTooltip
            style={{ marginLeft: '.5rem' }}
            message="Reduces the capacity by the number of Time Off hours taken during the period."
          />
        </CheckboxContainer>
      </Summary>
      <Breakdowns>
        <BreakdownItem to={utilizationByMember()}>
          <BreakdownLabel>Target Billable</BreakdownLabel>
          <BreakdownValue>
            <Percent value={targetBillable} minimumFractionDigits={1} maximumFractionDigits={1} />
          </BreakdownValue>
        </BreakdownItem>
        <BreakdownItem to={utilizationByMember()}>
          <BreakdownLabel>Attainment</BreakdownLabel>
          <BreakdownValue>
            <Percent value={attainment} minimumFractionDigits={1} maximumFractionDigits={1} />
          </BreakdownValue>
        </BreakdownItem>
      </Breakdowns>
    </Container>
  );
}
